import { Controller } from '@hotwired/stimulus';
import { FetchRequest } from '@rails/request.js';
import {
  eventListener,
  formatBytes,
} from '@inked/utilities';
import Mustache from 'mustache';

export class FileUploadController extends Controller {
  static targets = [
    'dropzone',
    'file',
    'files',
    'fileTemplate',
    'input',
    'selected',
  ];
  static values = {
    src: String,
  };

  connect() {
    this.stopInputEvent = eventListener(this.inputTarget, 'input', event => this.onInput(event));

    if (this.hasDropzoneTarget) {
      this.stopDragEvent = eventListener(this.dropzoneTarget, 'dragover', event => event.preventDefault());
      this.stopDropEvent = eventListener(this.dropzoneTarget, 'drop', event => this.onDrop(event));
    }
  }

  disconnect() {
    this.stopInputEvent();

    if (this.hasDropzoneTarget) {
      this.stopDragEvent();
      this.stopDropEvent();
    }
  }

  onDrop(event) {
    event.preventDefault();

    const data = new DataTransfer();

    for (const file of [...event.dataTransfer.files].slice(0, this.inputTarget.multiple ? undefined : 1)) {
      data.items.add(file);
    }

    this.inputTarget.files = data.files;
    this.inputTarget.dispatchEvent(new Event('input'));
  }

  async onInput(event) {
    event.preventDefault();

    const filelist = event.type === 'input' ? event.target.files : event.dataTransfer.files;

    if (this.inputTarget.multiple) {
      if (!this.srcValue) {
        this.filesTarget.innerHTML = '';
      }

      const files = [];

      [...filelist].forEach(file => {
        const index = this.hasFileTarget ? this.fileTargets.length : 0;

        this.filesTarget.insertAdjacentHTML(
          'beforeend',
          Mustache.render(this.fileTemplateTarget.innerHTML, {
            index,
            name: file.name,
            position: index + 1,
            size: formatBytes(file.size),
            thumbnail: URL.createObjectURL(file),
          }),
        );

        files.push(this.fileTargets[this.fileTargets.length - 1]);
      });

      if (this.srcValue) {
        const uploads = await this.upload(filelist);

        files.forEach((file, index) => {
          this.setAsyncFileData(file, uploads[index]);
        });
      }

      this.setSelected();

      return;
    }

    const file = filelist[0];

    this.setSelected(file);

    if (this.srcValue) {
      this.setAsyncFileData(this.fileTarget, (await this.upload([file]))[0]);
    }
  }

  remove(event) {
    const file = event.target.closest('[data-file-upload-target="file"]');

    if (file.querySelector('[name*="[_destroy]"]')) {
      file.querySelector('[name*="[_destroy]"]').value = 1;
      file.classList.add('hidden');
    } else {
      file.remove();
    }

    this.setSelected();
  }

  async upload(filelist) {
    const data = new FormData();

    [...filelist].forEach(file => {
      data.append('files[]', file);
    });

    const request = new FetchRequest(
      'post',
      this.srcValue,
      {
        body: data,
      },
    );
    const response = await request.perform();

    if (response.ok) {
      const json = await response.json;

      return json.files;
    }
  }

  setAsyncFileData(file, data) {
    if (data.errors) {
      this.dispatch('error', { detail: { errors: data.errors } });

      file.querySelector('[data-file-upload-spinner]').classList.add('hidden');
      file.querySelector('[data-file-upload-error]').classList.remove('hidden');

      return;
    }

    file.querySelector('[name*="[upload_file_id]"]').value = data.id;

    file.querySelector('[data-file-upload-spinner]').classList.add('hidden');
    file.querySelector('[data-file-upload-remove]').classList.remove('hidden');
  }

  setSelected(file = null) {
    if (file) {
      this.selectedTarget.innerHTML = Mustache.render(
        this.fileTemplateTarget.innerHTML,
        {
          index: 0,
          name: file.name,
          size: formatBytes(file.size),
          thumbnail: URL.createObjectURL(file),
        },
      );

      return;
    }

    this.selectedTarget.innerHTML = this.fileCount > 0
      ? `${this.fileCount} ${this.fileLabel} selected`
      : `No ${this.fileLabel} selected`;
  }

  get fileCount() {
    return this.fileTargets.filter(file => file.checkVisibility()).length;
  }

  get fileLabel() {
    return this.fileCount > 1 ? 'files' : 'file';
  }
}
